import { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { Rating } from '@/theme/components';
import { styled, TextArea, Stack, Text, Button } from '@/theme/index';
import { useReportRating } from './useReportRating';

const StyledRating = styled(Rating)(
  ({ theme }) => `
   .MuiRating-icon{
     color: ${theme.palette.primary.main};
     svg{
        width: 40px;
        height: 40px;
      }
    };
    p{
      margin-top: ${theme.spacing(1.5)};
    }
`
);

const StyledCard = styled('div')(({ theme }) => ({
  backgroundColor: '#fff',
  marginBottom: theme.spacing(2.5),
  width: '100%',
  position: 'relative',
  padding: theme.spacing(2),
  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 50%)',
}));

const StyledForm = styled('form')(() => ({
  width: '100%',
  maxWidth: '400px',
}));

type FormValues = {
  comments: string;
};

/**
 * When a report has no rating value it will render a controlled rating component allowing the user
 * to submit a rating for the report. When a report has been rated it will render a read-only
 * rating component
 */
export function ReportRating({
  rating,
  permalink,
}: {
  /** Report's rating value. */
  rating: number | null;
  /** Report's permalink. */
  permalink: string;
}) {
  const { trackEvent } = useAppConfigContext();
  const {
    images: { imgReportRating },
  } = useAppConstantsContext();

  const [rate, setRate] = useState({ rating, rateChanged: false });

  const reportRating = useReportRating(permalink);
  const { register, handleSubmit } = useForm<FormValues>();
  const onSubmit: SubmitHandler<FormValues> = (data) => {
    reportRating.mutate({
      permalink,
      rate: `${rate.rating}`,
      comments: data.comments,
    });
  };
  useEffect(() => {
    if (reportRating.isSuccess) {
      setRate({ ...rate, rateChanged: false });
    }
  }, [reportRating.isSuccess]);
  return (
    <StyledCard>
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={2}
      >
        <img src={imgReportRating} alt="" />
        {rate.rating && !rate.rateChanged ? (
          <>
            <Text
              variant="h1"
              align="center"
              sx={{ fontSize: { xs: 24, sm: 30 } }}
            >
              You rated this report as
            </Text>
            <StyledRating value={rate.rating} max={rate.rating} readOnly />
            <Text variant="body1">Rate out of 5 stars</Text>
          </>
        ) : (
          <>
            <Text
              variant="h1"
              align="center"
              sx={{ fontSize: { xs: 24, sm: 30 } }}
            >
              How would you rate this report
            </Text>
            <StyledRating
              value={rate.rating}
              onChange={(_event, newValue) => {
                trackEvent('rate report', 'submit', 'rate this report');
                setRate({ rating: newValue, rateChanged: true });
              }}
              className="rating-input-component"
            />
            <Text variant="body1">Out of 5 stars</Text>
            {rate.rateChanged && (
              <StyledForm onSubmit={handleSubmit(onSubmit)}>
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                >
                  <TextArea
                    placeholder="Add comments (optional)"
                    {...register('comments')}
                    fullWidth
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    fullWidth
                    disabled={reportRating.isLoading}
                  >
                    Submit your rating
                  </Button>
                </Stack>
              </StyledForm>
            )}
          </>
        )}
      </Stack>
    </StyledCard>
  );
}
